import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, 
  Calendar,
  LogOut,
  Truck,
  Settings,
  Filter,
  RefreshCw,
  Search
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { dashboardAPI, authAPI } from '../utils/api';
import KPICards from '../components/KPICards';
import CSVUploadModal from '../components/CSVUploadModal';
import SimpleFilters, { FilterState, defaultFilters } from '../components/SimpleFilters';
import Charts from '../components/Charts';
import DrillDownModal from '../components/DrillDownModal';
import type { DashboardResponse, User, DrillDownResponse } from '../types';
import logo from '../assets/logo.png';

export default function Dashboard() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const [dateType, setDateType] = useState<'delivery' | 'pickup'>('delivery');
  const [refreshing, setRefreshing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [viewAllData, setViewAllData] = useState<DrillDownResponse | null>(null);
  const [viewAllLoading, setViewAllLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [availableDrivers, setAvailableDrivers] = useState<string[]>([]);
  const [availableEquipmentTypes, setAvailableEquipmentTypes] = useState<string[]>([]);

  // Date range state (default to last 30 days)
  const [dateRange, setDateRange] = useState({
    from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd'),
  });

  // Load user from localStorage
  useEffect(() => {
    const storedUser = authAPI.getCurrentUser();
    if (!storedUser) {
      navigate('/login');
      return;
    }
    setUser(storedUser);
  }, [navigate]);

  // Load available drivers for filtering
  const loadAvailableDrivers = async () => {
    try {
      const data = await dashboardAPI.getAvailableDrivers();
      setAvailableDrivers(data.drivers);
    } catch (err) {
      console.error('Failed to load drivers:', err);
    }
  };

  // Load available equipment types for filtering
  const loadAvailableEquipmentTypes = async () => {
    try {
      const data = await dashboardAPI.getAvailableEquipmentTypes();
      setAvailableEquipmentTypes(data.equipment_types);
    } catch (err) {
      console.error('Failed to load equipment types:', err);
    }
  };

  // Load dashboard data
  const loadDashboardData = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      setError('');

      const apiParams = {
        date_from: dateRange.from,
        date_to: dateRange.to,
        date_type: dateType,
        excluded_drivers: filters.excludedDrivers,
        equipment_types: filters.selectedEquipmentTypes.length > 0 ? filters.selectedEquipmentTypes : undefined,
        rpm_exclusion_enabled: filters.rpmExclusionEnabled,
        rpm_exclusion_min: filters.rpmExclusionRange[0],
        rpm_exclusion_max: filters.rpmExclusionRange[1],
        rpm_min_enabled: filters.rpmMinEnabled,
        rpm_max_enabled: filters.rpmMaxEnabled,
      };
      
      console.log('📬 Sending API request with params:', apiParams);
      console.log('🚗 Excluded drivers:', filters.excludedDrivers);
      
      const data = await dashboardAPI.getDashboardData(apiParams);

      setDashboardData(data);
    } catch (err: any) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  // Load drivers and equipment types when user is available
  useEffect(() => {
    if (user) {
      loadAvailableDrivers();
      loadAvailableEquipmentTypes();
    }
  }, [user]);

  // Initialize filters with all equipment types selected when they're loaded
  useEffect(() => {
    if (availableEquipmentTypes.length > 0 && filters.selectedEquipmentTypes.length === 0) {
      setFilters(prev => ({
        ...prev,
        selectedEquipmentTypes: [...availableEquipmentTypes]
      }));
    }
  }, [availableEquipmentTypes]);

  // Initial load and when dependencies change
  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [dateRange, dateType, filters, user]);

  // Load all data for "View All" modal
  const loadAllData = async () => {
    setViewAllLoading(true);
    setShowViewAllModal(true);
    
    try {
      const params = new URLSearchParams({
        drill_type: 'all',
        value: 'all',
        date_from: dateRange.from,
        date_to: dateRange.to,
        date_type: dateType
      });
      
      // Add excluded drivers
      if (filters.excludedDrivers?.length) {
        filters.excludedDrivers.forEach(driver => {
          params.append('excluded_drivers', driver);
        });
      }
      
      // Add equipment types filter
      if (filters.selectedEquipmentTypes?.length) {
        filters.selectedEquipmentTypes.forEach(equipmentType => {
          params.append('equipment_types', equipmentType);
        });
      }
      
      // Add RPM filters
      if (filters.rpmMinEnabled) {
        params.append('rpm_min_enabled', 'true');
        params.append('rpm_exclusion_min', filters.rpmExclusionRange[0].toString());
      }
      if (filters.rpmMaxEnabled) {
        params.append('rpm_max_enabled', 'true');
        params.append('rpm_exclusion_max', filters.rpmExclusionRange[1].toString());
      }
      
      // Legacy RPM support
      if (filters.rpmExclusionEnabled && !filters.rpmMinEnabled && !filters.rpmMaxEnabled) {
        params.append('rpm_exclusion_enabled', 'true');
        params.append('rpm_exclusion_min', filters.rpmExclusionRange[0].toString());
        params.append('rpm_exclusion_max', filters.rpmExclusionRange[1].toString());
      }
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/drill-down?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data: DrillDownResponse = await response.json();
      setViewAllData(data);
      
    } catch (error) {
      console.error('Failed to fetch all data:', error);
      setShowViewAllModal(false);
    } finally {
      setViewAllLoading(false);
    }
  };
  
  // Refresh data
  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData(false);
  };

  // Logout handler
  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  // Quick date presets
  const datePresets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
  ];

  const applyDatePreset = (days: number) => {
    setDateRange({
      from: format(subDays(new Date(), days), 'yyyy-MM-dd'),
      to: format(new Date(), 'yyyy-MM-dd'),
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-netflix-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-netflix-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-netflix-grey-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-netflix-black">
      {/* Header */}
      <header className="bg-netflix-grey-800 border-b border-netflix-grey-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Title */}
            <div className="flex items-center space-x-4">
              <img src={logo} alt="Yulivan Carriers Logo" className="w-10 h-10" />
              <div>
                <h1 className="text-xl font-bold text-white">RPM Dashboard</h1>
                <p className="text-sm text-netflix-grey-400">Yulivan Carriers</p>
              </div>
            </div>

            {/* Header Actions */}
            <div className="flex items-center space-x-4">
              {/* Date Type Selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-netflix-grey-400">Show by:</span>
                <select
                  value={dateType}
                  onChange={(e) => setDateType(e.target.value as 'delivery' | 'pickup')}
                  className="bg-netflix-grey-700 text-white border border-netflix-grey-600 rounded px-3 py-1 text-sm focus:border-netflix-red outline-none"
                >
                  <option value="delivery">Delivery Date</option>
                  <option value="pickup">Pickup Date</option>
                </select>
              </div>

              {/* Filters Button */}
              <button
                onClick={() => setShowFiltersModal(true)}
                className="p-2 text-netflix-grey-400 hover:text-white transition-colors relative"
                title="Filter data"
              >
                <Filter className="w-5 h-5" />
                {(filters.excludedDrivers.length > 0 || filters.rpmMinEnabled || filters.rpmMaxEnabled || (filters.selectedEquipmentTypes.length > 0 && filters.selectedEquipmentTypes.length < availableEquipmentTypes.length)) && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-netflix-red rounded-full text-xs flex items-center justify-center text-white">
                    {filters.excludedDrivers.length + (filters.rpmMinEnabled ? 1 : 0) + (filters.rpmMaxEnabled ? 1 : 0) + (filters.selectedEquipmentTypes.length > 0 && filters.selectedEquipmentTypes.length < availableEquipmentTypes.length ? 1 : 0)}
                  </span>
                )}
              </button>
              
              {/* View All Data Button */}
              <button
                onClick={loadAllData}
                disabled={!dashboardData || isLoading}
                className="p-2 text-netflix-grey-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="View all dashboard data in table"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-netflix-grey-400 hover:text-white transition-colors"
                title="Refresh data"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Upload CSV Button */}
              <button 
                className="btn-primary flex items-center space-x-2"
                onClick={() => setShowUploadModal(true)}
              >
                <Upload className="w-4 h-4" />
                <span>Upload CSV</span>
              </button>

              {/* User Menu */}
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-white">{user.name}</p>
                  <p className="text-xs text-netflix-grey-400">{user.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-netflix-grey-400 hover:text-white transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Date Controls */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Performance Overview
              </h2>
              <p className="text-netflix-grey-400">
                {dashboardData ? (
                  `${format(new Date(dashboardData.period.from), 'MMM d')} - ${format(new Date(dashboardData.period.to), 'MMM d, yyyy')}`
                ) : (
                  'Loading date range...'
                )}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Quick Presets */}
              <div className="flex space-x-2">
                {datePresets.map((preset) => (
                  <button
                    key={preset.days}
                    onClick={() => applyDatePreset(preset.days)}
                    className="btn-secondary text-sm py-2 px-4"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Range */}
              <div className="flex space-x-2">
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="input-field text-sm"
                />
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="input-field text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
            {error}
            <button 
              onClick={() => loadDashboardData()}
              className="ml-4 underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* KPI Cards */}
        <KPICards 
          data={dashboardData?.kpis || {
            load_count: 0,
            total_revenue: 0,
            total_miles: 0,
            weighted_rpm: 0,
            median_rpm: 0,
          }} 
          isLoading={isLoading}
        />

        {/* Interactive Charts */}
        {!isLoading && dashboardData && (
          <Charts 
            data={dashboardData} 
            isLoading={isLoading}
            currentFilters={{
              dateFrom: dateRange.from,
              dateTo: dateRange.to,
              dateType: dateType,
              excludedDrivers: filters.excludedDrivers,
              equipmentTypes: filters.selectedEquipmentTypes.length > 0 ? filters.selectedEquipmentTypes : undefined,
              rpmExclusionEnabled: filters.rpmExclusionEnabled,
              rpmExclusionMin: filters.rpmExclusionRange[0],
              rpmExclusionMax: filters.rpmExclusionRange[1],
              rpmMinEnabled: filters.rpmMinEnabled,
              rpmMaxEnabled: filters.rpmMaxEnabled
            }}
          />
        )}
        {isLoading && (
          <Charts 
            data={{
              period: { from: '', to: '', date_type: 'delivery' },
              kpis: { load_count: 0, total_revenue: 0, total_miles: 0, weighted_rpm: 0, median_rpm: 0 },
              dispatchers: [],
              trend: []
            }} 
            isLoading={true} 
          />
        )}
      </main>
      
      {/* CSV Upload Modal */}
      <CSVUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          // Refresh dashboard data after successful upload
          loadDashboardData();
        }}
      />
      
      {/* Simple Filters Modal */}
      <SimpleFilters
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        onFiltersApply={(newFilters) => {
          setFilters(newFilters);
          // Note: useEffect will automatically trigger loadDashboardData when filters change
        }}
        availableDrivers={availableDrivers}
        availableEquipmentTypes={availableEquipmentTypes}
        currentFilters={filters}
      />
      
      {/* View All Data Modal */}
      <DrillDownModal
        isOpen={showViewAllModal}
        onClose={() => {
          setShowViewAllModal(false);
          setViewAllData(null);
        }}
        data={viewAllData}
        isLoading={viewAllLoading}
        dateType={dateType}
      />
    </div>
  );
}
